/**
 * GoodGuardian — Court Report Screen
 *
 * Lets users configure a date range and choose which sections to include
 * before generating a court-ready PDF via the browser's print dialog.
 */
import { useState, useEffect, useMemo } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { useApp } from '@/context/AppContext'
import { data as dataApi } from '@/utils/auth'
import { printCourtReport } from '@/utils/printUtils'

function todayStr() {
  return new Date().toISOString().split('T')[0]
}

function monthsAgoStr(n) {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return d.toISOString().split('T')[0]
}

const QUICK_RANGES = [
  { label: '30 days',   from: () => monthsAgoStr(1)  },
  { label: '3 months',  from: () => monthsAgoStr(3)  },
  { label: '6 months',  from: () => monthsAgoStr(6)  },
  { label: '1 year',    from: () => monthsAgoStr(12) },
  { label: 'All time',  from: () => ''               },
]

const SECTIONS = [
  { key: 'comms',    label: 'Communications Log', emoji: '💬', color: 'bg-blue-100 text-blue-700'    },
  { key: 'events',   label: 'Custody Events',      emoji: '📋', color: 'bg-red-100 text-red-700'     },
  { key: 'calendar', label: 'Calendar Events',     emoji: '📅', color: 'bg-green-100 text-green-700' },
  { key: 'expenses', label: 'Expense Log',         emoji: '🧾', color: 'bg-amber-100 text-amber-700' },
  { key: 'children', label: 'Children Profiles',   emoji: '👶', color: 'bg-purple-100 text-purple-700' },
]

export default function CourtReport() {
  const { user, comms = [], events = [], calendarEvents = [], kidProfiles = [] } = useApp()

  const [dateFrom,   setDateFrom]   = useState(monthsAgoStr(6))
  const [dateTo,     setDateTo]     = useState(todayStr())
  const [enabled,    setEnabled]    = useState({ comms: true, events: true, calendar: true, expenses: true, children: true })
  const [expenses,   setExpenses]   = useState([])
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    dataApi.getExpenses?.()
      .then(d => setExpenses(d?.expenses || []))
      .catch(() => {})
  }, [])

  const filtered = useMemo(() => {
    const inRange = (dateStr) =>
      (!dateFrom || dateStr >= dateFrom) && (!dateTo || dateStr <= dateTo)
    return {
      comms:    comms.filter(e => inRange(e.date || '')),
      events:   events.filter(e => inRange(e.date || '')),
      calendar: calendarEvents.filter(e => inRange(e.start_date || '')),
      expenses: expenses.filter(e => inRange(e.purchase_date || '')),
    }
  }, [comms, events, calendarEvents, expenses, dateFrom, dateTo])

  function countFor(key) {
    if (key === 'children') return kidProfiles.length
    return filtered[key]?.length ?? 0
  }

  const totalRecords = SECTIONS.reduce((sum, s) => sum + (enabled[s.key] ? countFor(s.key) : 0), 0)

  function toggleSection(key) {
    setEnabled(prev => ({ ...prev, [key]: !prev[key] }))
  }

  function handleGenerate() {
    setGenerating(true)
    try {
      printCourtReport({
        userName:       user?.name,
        state:          user?.state,
        comms:          enabled.comms     ? filtered.comms    : [],
        events:         enabled.events    ? filtered.events   : [],
        calendarEvents: enabled.calendar  ? filtered.calendar : [],
        expenses:       enabled.expenses  ? filtered.expenses : [],
        children:       enabled.children  ? kidProfiles       : [],
        currentUserId:  user?.id,
        dateFrom,
        dateTo,
      })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Court Report" showBack />

      <div className="screen no-scrollbar px-4 py-5 space-y-4">

        {/* ── Date Range ── */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Date Range</h3>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">To</label>
              <input
                type="date"
                value={dateTo}
                max={todayStr()}
                onChange={e => setDateTo(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {QUICK_RANGES.map(opt => (
              <button
                key={opt.label}
                onClick={() => {
                  setDateFrom(opt.from())
                  setDateTo(opt.label === 'All time' ? '' : todayStr())
                }}
                className="text-xs px-3 py-1.5 rounded-full bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/40 font-medium transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Section Toggles ── */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Include in Report</h3>
          <div className="space-y-2">
            {SECTIONS.map(s => {
              const count = countFor(s.key)
              const on    = enabled[s.key]
              return (
                <button
                  key={s.key}
                  onClick={() => toggleSection(s.key)}
                  className={[
                    'w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left',
                    on
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800',
                  ].join(' ')}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{s.emoji}</span>
                    <span className={`text-sm font-medium ${on ? 'text-brand-700 dark:text-brand-300' : 'text-slate-500 dark:text-slate-400'}`}>
                      {s.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {count > 0 ? (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.color}`}>
                        {count}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 dark:text-slate-500">none</span>
                    )}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      on ? 'border-brand-500 bg-brand-500' : 'border-slate-300 dark:border-slate-600'
                    }`}>
                      {on && (
                        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Summary Preview ── */}
        <div className="card p-4 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-2">
            <span>📊</span>
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Report Preview</span>
          </div>
          {totalRecords === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">
              No records match the current filters. Adjust the date range or enable more sections.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                <span className="font-bold text-brand-700 dark:text-brand-300">{totalRecords}</span>{' '}
                record{totalRecords !== 1 ? 's' : ''} will be included in the PDF.
              </p>
              <div className="flex flex-wrap gap-2">
                {SECTIONS.map(s => {
                  const count = countFor(s.key)
                  if (!enabled[s.key] || count === 0) return null
                  return (
                    <span key={s.key} className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
                      {s.emoji} {count} {s.label.toLowerCase()}
                    </span>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Disclaimer ── */}
        <div className="disclaimer">
          <p className="text-xs leading-relaxed">
            <strong>⚠️ Attorney review required.</strong> This PDF compiles your logged records for the selected date range. It is not a finalized legal document. Share with your attorney before submitting to any court.
          </p>
        </div>

        {/* ── Generate Button ── */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={totalRecords === 0}
          loading={generating}
          onClick={handleGenerate}
        >
          🖨️ Generate PDF
        </Button>

        <div className="pb-4" />
      </div>
    </div>
  )
}
